import { isPinEvent } from "./pinEventClaim";

const PIN_RELEVANT_AFTER_LEAVING = new Set(["mousemove", "mouseover"]);

// Which map event to trigger for an overlay event, if any. After leaving a pin one mousemove is sent
// so the pins' pointer hook sees "no pin" and clears the hover.
export const getPinForwardEvent = (
  eventName: string,
  isPinHit: boolean,
  wasForwarding: boolean
): string | undefined => {
  // A map mouseout means the pointer left the map, so it would clear a pin still under the pointer.
  if (eventName === "mouseout") {
    return undefined;
  }
  if (isPinHit) {
    return eventName;
  }
  return wasForwarding && PIN_RELEVANT_AFTER_LEAVING.has(eventName) ? "mousemove" : undefined;
};

// Clickable overlays (trails, polygons, Data features) swallow pointer events over a deck pin.
// Returns true when the event was handed to the map's pin listeners instead.
export const createPinForwarder = (map: google.maps.Map) => {
  let wasForwarding = false;
  return (eventName: string, event: google.maps.MapMouseEvent): boolean => {
    const isPinHit = isPinEvent(map, event);
    const forwardEvent = getPinForwardEvent(eventName, isPinHit, wasForwarding);
    wasForwarding = isPinHit;
    if (!forwardEvent) {
      return false;
    }
    google.maps.event.trigger(map, forwardEvent, event);
    return true;
  };
};

interface ForwardPinEventsOptions<E> {
  eventNames?: string[];
  onEvent?: (eventName: string, event: E, forwarded: boolean) => void;
}

// Adds the overlay listeners that hand pin events on to the map; onEvent then sees every event with
// whether a pin took it. Returns the listener handles for cleanup.
export const forwardPinEvents = <E extends google.maps.MapMouseEvent>(
  target: Pick<google.maps.MVCObject, "addListener">,
  forward: (eventName: string, event: E) => boolean,
  {
    eventNames = ["click", "dblclick", "mousedown", "mousemove"],
    onEvent,
  }: ForwardPinEventsOptions<E> = {}
) =>
  eventNames.map((eventName) =>
    target.addListener(eventName, (event: E) => {
      const forwarded = forward(eventName, event);
      onEvent?.(eventName, event, forwarded);
    })
  );
